import { WarningAmber, WorkspacePremiumOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { IconTypography } from '@procureai/ui-mui';
import { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DemandNegotiatedQuoteItem,
  DemandRequestLineItem,
  LineItemNegotiationCaseAssignments,
} from '../../DemandAwardingWidget.hooks';
import { DemandNegotiationCase } from '../../DemandAwardingWidget.types';
import { ItemSelectionCell } from '../ItemSelectionCell/ItemSelectionCell';
import { TsrctQuoteItemStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { NULLISH_CELL_VALUE } from '~/components/DataGridCustom/DataGridCustom.types';

import { getNegotiationFailedReasonLabel, getQuoteItemStatusLabel } from './QuoteItemCell.i18n';

export interface QuoteItemCellProps<R extends GridValidRowModel = any> {
  quoteItem?: DemandNegotiatedQuoteItem;
  row?: R;
  lineItemAssignments: LineItemNegotiationCaseAssignments;
  negotiationCase: DemandNegotiationCase;
  demandRequestItem: DemandRequestLineItem;
}

export const SelectableQuoteItemCell = (props: QuoteItemCellProps) => {
  const { quoteItem, lineItemAssignments, negotiationCase, demandRequestItem } = props;
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });

  if (!quoteItem) {
    return null;
  }

  const adornment: ReactNode = undefined;

  const warning =
    quoteItem.status === TsrctQuoteItemStatus.FINAL_OFFER_PROPOSED ? (
      <IconTypography startIcon={<WarningAmber color="warning" />} color="warning" variant="body2" noWrap={false}>
        {t('cell.itemSelection.finalOfferProposed')}
      </IconTypography>
    ) : null;

  return (
    <Stack spacing={2} width="100%">
      <ItemSelectionCell
        checked={lineItemAssignments.isLineItemAssignedToNegotiationCase(demandRequestItem.id, negotiationCase)}
        onToggle={() => lineItemAssignments.toggleLineItemAssignment(demandRequestItem.id, negotiationCase)}
        label={t('cell.itemSelection.awardThisItem')}
        radioStart={adornment}
      />
      {warning}
    </Stack>
  );
};

export const DefaultQuoteItemCell = (props: QuoteItemCellProps) => {
  if (!props.quoteItem) {
    return null;
  }

  if (props.negotiationCase.isAwardable && props.quoteItem.isAwardable) {
    return <SelectableQuoteItemCell {...props} />;
  }

  return <StatusQuoteItemCell {...props} />;
};

export const StatusQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });

  if (!quoteItem) {
    return null;
  }

  return (
    <Typography component="div" variant="body2" color="text.secondary">
      {getQuoteItemStatusLabel(t, quoteItem.status)}
    </Typography>
  );
};

export const AwardedQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });

  if (!quoteItem) {
    return null;
  }

  return (
    <IconTypography
      startIcon={<WorkspacePremiumOutlined color="success" />}
      color="success"
      variant="body2"
      fontWeight="bold"
    >
      {getQuoteItemStatusLabel(t, quoteItem.status)}
    </IconTypography>
  );
};

export const NegotiationFailedQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });
  return (
    <Typography component="div" variant="body2" color={'text.secondary'}>
      {getNegotiationFailedReasonLabel(t, quoteItem?.negotiationFailedReason, quoteItem?.negotiationFailedReasonText)}
    </Typography>
  );
};

const QUOTE_ITEM_CELL_STATES: Record<TsrctQuoteItemStatus, ComponentType<QuoteItemCellProps>> = {
  [TsrctQuoteItemStatus.REQUESTED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.NOT_REQUESTED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.BID_PROVIDED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.BID_DECLINED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.AGREEMENT_REACHED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.NEGOTIATION_FAILED]: NegotiationFailedQuoteItemCell,
  [TsrctQuoteItemStatus.AWARDED]: AwardedQuoteItemCell,
  [TsrctQuoteItemStatus.OFFER_DISMISSED]: DefaultQuoteItemCell,
  [TsrctQuoteItemStatus.FINAL_OFFER_PROPOSED]: DefaultQuoteItemCell,
};

export const QuoteItemCell = <R extends GridValidRowModel = any>(props: QuoteItemCellProps<R>) => {
  if (!props.quoteItem) {
    return <>{NULLISH_CELL_VALUE}</>;
  }

  const Component = QUOTE_ITEM_CELL_STATES[props.quoteItem.status];
  return <Component {...props} />;
};
